import { Button } from "@/components/ui/Button";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSessionContext } from "@/context/SessionContext";
import { fetchPromoCodeInfo } from "@/utils";
import { useEffect, useState } from "react";

const PromoCodeField = () => {
  const { language } = useLanguage();
  const {
    registrationPromoCode,
    registrationPromoInfo,
    setRegistrationPromoCode,
    setRegistrationPromoInfo,
    setIsPromoCodeChecking,
    setSelectedProduct,
  } = useSessionContext();
  const { userInfoGlobal } = useAppContext();
  const copy = {
    cs: {
      hasPromoCode: "Mám promo kód",
      promoCode: "Promo kód",
      promoCodePlaceholder: "Zadejte promo kód",
      checking: "Ověřuji promo kód...",
      applied: "Promo kód byl přijat.",
      invalid: "Promo kód není platný.",
      noLongerValid: "Promo kód již není platný.",
    },
    en: {
      hasPromoCode: "I have a promo code",
      promoCode: "Promo code",
      promoCodePlaceholder: "Enter promo code",
      checking: "Checking promo code...",
      applied: "Promo code applied.",
      invalid: "Promo code is not valid.",
      noLongerValid: "Promo code is no longer valid.",
    },
  }[language];
  const [showInput, setShowInput] = useState(false);
  const [hasCheckedCode, setHasCheckedCode] = useState(false);

  useEffect(() => {
    const promoCode = registrationPromoCode.trim();
    setRegistrationPromoInfo(null);
    setHasCheckedCode(false);

    if (!promoCode || !userInfoGlobal?.email || !userInfoGlobal.token) {
      setIsPromoCodeChecking(false);
      return;
    }

    let cancelled = false;
    setIsPromoCodeChecking(true);
    const timeout = window.setTimeout(async () => {
      try {
        const response = await fetchPromoCodeInfo(
          promoCode,
          userInfoGlobal.email,
          userInfoGlobal.token,
        );
        if (cancelled) return;
        const promoInfo = response.status === 200 ? response.data.data : null;
        setRegistrationPromoInfo(promoInfo);
        setHasCheckedCode(true);
        if (promoInfo?.valid && promoInfo.membership) {
          setSelectedProduct(promoInfo.membership);
        }
      } catch {
        if (!cancelled) {
          setRegistrationPromoInfo(null);
          setHasCheckedCode(true);
        }
      } finally {
        if (!cancelled) {
          setIsPromoCodeChecking(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [
    registrationPromoCode,
    setIsPromoCodeChecking,
    setRegistrationPromoInfo,
    setSelectedProduct,
    userInfoGlobal?.email,
    userInfoGlobal?.token,
  ]);

  return (
    <div>
      {/* Ghost link to reveal promo code input */}
      {!showInput && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowInput(true)}
        >
          {copy.hasPromoCode}
        </Button>
      )}

      {/* Promo code input, only visible after clicking */}
      {showInput && (
        <label className="app-form-field">
          {copy.promoCode}
          <input
            type="text"
            id="promoCode"
            name="promoCode"
            placeholder={copy.promoCodePlaceholder}
            value={registrationPromoCode}
            onChange={(event) => {
              setRegistrationPromoCode(event.target.value.toUpperCase());
            }}
            className="h-12 rounded-lg border border-softiq-border bg-softiq-surface px-4 text-base font-normal text-softiq-copy outline-none transition focus:border-softiq-secondary focus:ring-2 focus:ring-softiq-secondary/15"
          />
          {registrationPromoCode.trim() ? (
            <span
              className={`text-xs ${
                registrationPromoInfo?.valid
                  ? "text-emerald-700"
                  : hasCheckedCode
                    ? "text-red-600"
                    : "text-softiq-copy-muted"
              }`}
            >
              {!hasCheckedCode
                ? copy.checking
                : registrationPromoInfo?.valid
                  ? copy.applied
                  : registrationPromoInfo?.reason === "MAX_REDEMPTIONS"
                    ? copy.noLongerValid
                    : copy.invalid}
            </span>
          ) : null}
        </label>
      )}
    </div>
  );
};

export default PromoCodeField;
